import { Component, effect, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { GlobalConstants } from '../../../shared/global-constants';
import { VisualBaseComponent } from '../visual-base.component';
import { Peer, StateService, SubjectType } from '../../services/state.service';
import { ChartInfoBoxComponent } from '../chart-info-box/chart-info-box.component';

@RegisterComponent('PieChart')
@Component({
  selector: 'app-pie-distribution',
  standalone: true,
  imports: [CommonModule, NgxEchartsDirective, ChartInfoBoxComponent],
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss',
  providers: [provideEcharts()]
})
export class PieChartComponent extends VisualBaseComponent {
  elementId = input.required<string>();
  subjectType = input.required<SubjectType>();
  sort = input<string>('amount');
  numElements = input<number>(5);

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;

  override loadingColor = '#ebebeb';
  override initOptions = { height: 200 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 0,
      top: 20,
      bottom: 40,
      containLabel: true
    },
    title: {
      show: false
    },
    tooltip: {
      trigger: 'item'
    },
    legend: {
      orient: 'vertical',
      left: 'right',
      top: '10%',
      selectedMode: false
    },
    series: [
      {
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#ebebeb',
          borderWidth: 2
        },
        labelLine: {
          show: true,
          length: 10
        },
        center: ['30%', '50%'],
        color: GlobalConstants.pieColors
      }
    ]
  };

  constructor() {
    super();

    effect(() => {
      const subjectType = this.subjectType();
      const peer = subjectType == SubjectType.peer ? this.peer() : undefined;
      const date = this.dateRange()?.endDate;
      const elementId = this.elementId();
      const group = this.filteredGroup();
      if (this.chartInit() && subjectType && (peer || subjectType == SubjectType.institution) && date && elementId && group) {
        this.updateVisual(peer, date, elementId);
      }
    });
  }

  public updateVisual(peer: Peer | undefined, date: Date, elementId: string) {
    const metricData: { name: string, value: number, itemStyle: { color: string } }[] = [];
    this.metricService.getChartElements(elementId, date, this.sort(), this.numElements()).subscribe(chartElements => {
      this.metricService.getElementData(this.institution().iid, elementId, peer).subscribe(metrics => {
        let other = 0;
        let index = 0;
        metrics.forEach((metric) => {
          const dataRow = metric.data.findIndex((r) => { return new Date(r[0]).getTime() == date.getTime(); });

          let indexes: number[] = [];
          if (this.subjectType() == SubjectType.peer) {
            indexes = this.visualService.getPeerIndexes(metric.data[0]);
          } else {
            indexes.push(metric.data[0].findIndex((c: number) => {
              return c == this.institution().iid;
            }));
          }

          let sum = 0; let count = 0;
          indexes.forEach(i => {
            if (dataRow > -1 && metric.data[dataRow][i] != null) {
              sum += metric.data[dataRow][i]; count++;
            }
          });
          const value: number = count > 0 ? sum / count : 0;

          const chartElement = chartElements.find((slice) => { return slice.id == metric.metricId });
          if (chartElement?.plot == true && metric.rowTitle != 'Other') {
            const color: string = this.chartColors().find(c => c.metricId == metric.metricId)?.color ?? GlobalConstants.pieColors[index];
            if (value > 0) {
              metricData[index++] = {
                name: metric.rowTitle, value: value, itemStyle: { color: color }
              };
            }
          }
          else {
            other += value;
          }
        });

        if (other > 0) {
          const color: string = this.chartColors().find(c => c.metricId == 'Other')?.color ?? GlobalConstants.pieColors[index];
          metricData[index] = {
            name: 'Other', value: other, itemStyle: { color: color }
          };
        }

        if (metricData.length == 0) {
          this.echartsInstance?.clear();
          this.chartOptions = this.noDataOptions;
        }
        else {
          this.updateOptions = {
            title: {
              show: false
            },
            tooltip: {
              trigger: 'item',
              formatter: function (params: any) {
                return '<div style="margin: 0px 0 0;line-height:1;"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color: ' + params.color + ';"></span><span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">' + params.name + '</span><span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">' + (params.value / 1000).toLocaleString() + 'M (' + params.percent + '%)</span><div style="clear:both"></div></div>';
              }
            },
            legend: {
              orient: 'vertical',
              left: 'right',
              top: '10%',
              selectedMode: false
            },
            series: [
              {
                type: 'pie',
                radius: ['40%', '70%'],
                avoidLabelOverlap: true,
                itemStyle: {
                  borderRadius: 10,
                  borderColor: '#ebebeb',
                  borderWidth: 2
                },
                labelLine: {
                  show: true,
                  length: 10
                },
                center: ['30%', '50%'],
                label: {
                  formatter: function (params: any) {
                    return params.percent.toFixed(1) + "%";
                  }
                },
                data: metricData
              }
            ]
          };
        }
        if (!this.echartsInstance?.isDisposed()) {
          this.echartsInstance?.hideLoading();
        }
      });
    });
  }
}
